import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { 
  Users, Plus, Search, Mail, Phone, Shield, Edit, Trash2, 
  RefreshCw, ToggleLeft, ToggleRight, Loader2, CheckSquare
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DUTY_OPTIONS = [
  { key: 'manage_trips',    label: 'Manage Trips (Create, Edit, Cancel, Delete Trips)' },
  { key: 'manage_routes',   label: 'Manage Routes' },
  { key: 'manage_stations', label: 'Manage Stations' },
  { key: 'manage_buses',    label: 'Manage Buses' },
  { key: 'manage_users',    label: 'Manage Users & Staff' },
  { key: 'manage_expenses', label: 'Manage Expenses & Float' },
  { key: 'manage_luggage',  label: 'Manage Luggage & Parcels' },
  { key: 'gps_tracking',    label: 'Live GPS Tracking' },
  { key: 'activity_logs',   label: 'View Activity & Audit Logs' },
  { key: 'print_history',   label: 'View Print History' },
];

const AdminUsers = () => {
  const { token, isOverseer } = useAuth();
  const [users, setUsers] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [dutiesDialogOpen, setDutiesDialogOpen] = useState(false);
  const [dutiesUser, setDutiesUser] = useState(null);
  const [selectedDuties, setSelectedDuties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'conductor',
    station_id: ''
  });

  const [editUser, setEditUser] = useState({
    full_name: '',
    phone: '',
    role: '',
    station_id: ''
  });

  const roles = isOverseer() ? [
    { value: 'overseer', label: '👑 Superuser / Overseer' },
    { value: 'station_superuser', label: '🏢 Station Superuser' },
    { value: 'conductor', label: '🚌 Host' },
    { value: 'driver', label: '🚗 Driver' },
  ] : [
    { value: 'station_superuser', label: '🏢 Station Superuser' },
    { value: 'conductor', label: '🚌 Host' },
    { value: 'driver', label: '🚗 Driver' },
  ];

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const [usersRes, stationsRes] = await Promise.all([
        axios.get(`${API_URL}/users`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API_URL}/stations`)
      ]);
      setUsers(usersRes.data);
      setStations(stationsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.full_name || !newUser.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created successfully!');
      setDialogOpen(false);
      setNewUser({
        email: '',
        password: '',
        full_name: '',
        phone: '',
        role: 'conductor',
        station_id: ''
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/users/${selectedUser.id}`, editUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully!');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/users/${userId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openDutiesDialog = (user) => {
    setDutiesUser(user);
    setSelectedDuties(user.delegated_duties || []);
    setDutiesDialogOpen(true);
  };

  const handleAssignDuties = async () => {
    if (!dutiesUser) return;
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/users/${dutiesUser.id}/delegated-duties`,
        { delegated_duties: selectedDuties },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Duties updated successfully!');
      setDutiesDialogOpen(false);
      setDutiesUser(null);
      fetchData();
    } catch (error) {
      console.error('Failed to assign duties:', error);
      toast.error(error.response?.data?.detail || 'Failed to update duties');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleDuty = (key) => {
    setSelectedDuties(prev =>
      prev.includes(key) ? prev.filter(d => d !== key) : [...prev, key]
    );
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditUser({
      full_name: user.full_name,
      phone: user.phone,
      role: user.role,
      station_id: user.station_id || ''
    });
    setEditDialogOpen(true);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      overseer: 'bg-purple-100 text-purple-700',
      station_superuser: 'bg-blue-100 text-blue-700',
      conductor: 'bg-green-100 text-green-700',
      driver: 'bg-orange-100 text-orange-700',
      passenger: 'bg-slate-100 text-slate-700'
    };
    return colors[role] || colors.passenger;
  };

  const filteredUsers = users.filter(user => 
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone?.includes(searchTerm)
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-users-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              Manage Users
            </h1>
            <p className="text-slate-600">
              Create and manage staff accounts
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData} data-testid="refresh-users-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-panashe" data-testid="add-user-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Staff Member
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Staff Member</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      placeholder="Enter full name"
                      value={newUser.full_name}
                      onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      placeholder="email@panashecoaches.co.zw"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      placeholder="+263 77 123 4567"
                      value={newUser.phone}
                      onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label>Role *</Label>
                    <Select 
                      value={newUser.role}
                      onValueChange={(val) => setNewUser({...newUser, role: val})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(role => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Station</Label>
                    <Select 
                      value={newUser.station_id}
                      onValueChange={(val) => setNewUser({...newUser, station_id: val})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select station" />
                      </SelectTrigger>
                      <SelectContent>
                        {stations.map(station => (
                          <SelectItem key={station.id} value={station.id}>
                            {station.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    className="w-full btn-panashe"
                    onClick={handleCreateUser}
                    disabled={submitting}
                    data-testid="submit-user-btn"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Create User
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Overseer Guidance Banner */}
        {isOverseer() && (
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-3">
            <Shield className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-bold text-purple-900">👑 Superuser &amp; Delegated Permissions Control</p>
              <p className="text-purple-700 text-xs mt-0.5">
                As a Superuser / Overseer, you can create additional Superusers/Overseers, Station Superusers, and Hosts/Drivers.
                Use the <strong>"Assign Duties"</strong> button on any staff row to grant or revoke specific permissions (Manage Trips, Manage Routes, Manage Expenses, Live GPS Tracking, etc.).
              </p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-users-input"
          />
        </div>

        {/* Users List */}
        <Card className="card-panashe">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Users className="h-5 w-5" />
              Staff Members ({filteredUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin text-panashe-blue mx-auto mb-2" />
                <p className="text-slate-500">Loading users...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Station</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Permissions / Duties</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredUsers.map(user => (
                      <tr key={user.id} className="hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-panashe-blue rounded-full flex items-center justify-center text-white font-bold">
                              {user.full_name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-medium">{user.full_name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-1 text-slate-600">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </div>
                            <div className="flex items-center gap-1 text-slate-600">
                              <Phone className="h-3 w-3" />
                              {user.phone}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={getRoleBadgeColor(user.role)}>
                            <Shield className="h-3 w-3 mr-1" />
                            {user.role?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {user.station_name || '-'}
                        </td>
                        <td className="px-4 py-3">
                          {user.role === 'overseer' ? (
                            <Badge className="bg-purple-100 text-purple-800 font-semibold border border-purple-200 text-xs">
                              👑 Full Superuser Access
                            </Badge>
                          ) : (
                            <div className="space-y-1">
                              {user.delegated_duties?.length > 0 ? (
                                <div className="flex flex-wrap gap-1 max-w-xs">
                                  {user.delegated_duties.map(d => (
                                    <Badge key={d} variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                                      {DUTY_OPTIONS.find(opt => opt.key === d)?.label?.split(' (')[0] || d}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400 text-xs italic">Standard role</span>
                              )}
                              {isOverseer() && (
                                <div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openDutiesDialog(user)}
                                    className="h-6 text-[11px] px-2 border-purple-300 text-purple-700 hover:bg-purple-50 flex items-center gap-1 mt-1 font-semibold"
                                  >
                                    <CheckSquare className="h-3 w-3 text-purple-600" />
                                    Assign Duties ({user.delegated_duties?.length || 0})
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={user.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {user.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditDialog(user)}
                              data-testid={`edit-user-${user.id}`}
                              title="Edit User"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {user.role !== 'overseer' && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleToggleStatus(user.id, user.is_active !== false)}
                                  data-testid={`toggle-user-${user.id}`}
                                  title="Toggle Active Status"
                                >
                                  {user.is_active !== false ? 
                                    <ToggleRight className="h-4 w-4 text-green-600" /> : 
                                    <ToggleLeft className="h-4 w-4 text-slate-400" />
                                  }
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="text-red-600 hover:text-red-700"
                                  data-testid={`delete-user-${user.id}`}
                                  title="Delete User"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={editUser.full_name}
                  onChange={(e) => setEditUser({...editUser, full_name: e.target.value})}
                />
              </div>

              <div>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={editUser.phone}
                  onChange={(e) => setEditUser({...editUser, phone: e.target.value})}
                />
              </div>

              <div>
                <Label>Role</Label>
                <Select 
                  value={editUser.role}
                  onValueChange={(val) => setEditUser({...editUser, role: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Station</Label>
                <Select 
                  value={editUser.station_id}
                  onValueChange={(val) => setEditUser({...editUser, station_id: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select station" />
                  </SelectTrigger>
                  <SelectContent>
                    {stations.map(station => (
                      <SelectItem key={station.id} value={station.id}>
                        {station.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button 
                className="w-full btn-panashe"
                onClick={handleEditUser}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Update User
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Duties Assignment Dialog */}
        <Dialog open={dutiesDialogOpen} onOpenChange={setDutiesDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2 text-purple-900">
                <CheckSquare className="h-5 w-5 text-purple-600" />
                Assign Overseer Duties
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-xs text-slate-600">
                Delegate specific overseer / admin duties to station superuser <strong>{dutiesUser?.full_name}</strong>. Selected sections will be accessible in their navigation sidebar.
              </p>
              
              <div className="space-y-2 border rounded-lg p-3 bg-slate-50 max-h-80 overflow-y-auto">
                {DUTY_OPTIONS.map(duty => {
                  const isChecked = selectedDuties.includes(duty.key);
                  return (
                    <div 
                      key={duty.key}
                      onClick={() => toggleDuty(duty.key)}
                      className={`flex items-center justify-between p-2.5 rounded-md border cursor-pointer transition-colors ${
                        isChecked ? 'bg-purple-50 border-purple-300 text-purple-900 font-medium' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="text-sm">{duty.label}</span>
                      <input 
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {}} // handled by parent div
                        className="h-4 w-4 text-purple-600 rounded border-slate-300 pointer-events-none"
                      />
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setDutiesDialogOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={handleAssignDuties}
                  disabled={submitting}
                  data-testid="save-duties-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Delegated Duties
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Info Card */}
        <Card className="card-panashe bg-slate-50">
          <CardContent className="p-6">
            <h3 className="font-heading font-bold mb-3">User Roles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <Badge className="bg-purple-100 text-purple-700">Overseer</Badge>
                <span className="text-slate-600">Full system access, can manage all stations and assign duties</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-blue-100 text-blue-700">Station Superuser</Badge>
                <span className="text-slate-600">Manages specific station operations and delegated admin duties</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-green-100 text-green-700">Host</Badge>
                <span className="text-slate-600">Check-in passengers, view manifests, print thermal tickets</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-orange-100 text-orange-700">Driver</Badge>
                <span className="text-slate-600">Trip management, GPS tracking</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminUsers;
